import React from 'react';
import Auth from '@aws-amplify/auth'
import { Hub, Logger } from '@aws-amplify/core'
import { Provider as ReduxProvider } from 'react-redux';
import { BrowserChecker } from '@alucio/video'
import { WebLinking as Linking } from '@alucio/core'
import store from './state/redux/store';
import {
  LuxTheming,
  AlucioTheme,
  withToastProvider,
} from '@alucio/lux-ui';
import { deleteDB } from 'idb';
import { withLDProvider } from 'launchdarkly-react-client-sdk';
import { Router } from './router';
import Routes, { RoutesListener } from './router/routes';
import DevLogger from 'src/components/DevLogger/DevLogger'
import config from 'src/config/app.json';

// COMPONENTS
import AppContainer from 'src/AppContainer';
import { ErrorBoundary } from 'react-error-boundary'
import Error from 'src/components/DNA/Error'
import Authenticator from './components/Authenticator';
import Idle from 'src/components/IdleComponent/IdleComponent';
import ContentPreviewModal from './components/ContentPreviewModal/ContentPreviewModal';
import UserInit from 'src/components/UserInit/UserInit';
import RightSideBar from 'src/components/Sidebar/RightSideBar';
import DNAConnectedModal from 'src/components/DNA/Modal/DNAConnectedModal'
import AppSettings from 'src/state/context/AppSettings'
import DNAAppContainer from 'src/components/DNA/AppContainer/DNAAppContainer'
import SlideSelector from './components/SlideSelector/SlideSelector';
import CacheDB from './worker/db/cacheDB';
import PresentationBuilder from './components/PresentationBuilder/PresentationBuilder';

const logger = new Logger('ServiceWorker', 'INFO')
const UnsupportedBrowserImage = require('../../beacon/assets/images/UnsupportedBrowser.svg');

// [TODO] - Consider centralizing logout relating functions in one place (e.g. src/components/Authenticator)
// [TODO-PWA] - Consider moving this to the service worker layer instead
const logout = async () => {
  const isPWAStandalone = window.matchMedia('(display-mode: standalone)').matches

  window.localStorage.clear();
  analytics?.track('LOGIN_LOGOUT_FORCED', {
    action: 'LOGOUT_FORCED',
    category: 'LOGIN',
  });

  // Nuke all IndexDBs we use this function instead of datastore as a fail safe
  const dbsToDelete = ['amplify-datastore', 'analyticsOffline']
  if (!isPWAStandalone) {
    dbsToDelete.push('CacheDB')
    // De-register the service worker if it's registered
    const registration = await navigator.serviceWorker.getRegistration('/')
    if (registration) {
      logger.info('Unregistering service worker', registration)
      const isUnregistered = await registration?.unregister()
      logger.info('SW Unregistered', isUnregistered)
    }
  } else {
    try {
      const cacheDB = new CacheDB()
      await cacheDB.open()
      await cacheDB.purge()
      await cacheDB.close()
    } catch (e) {
      console.warn('Error occured purging cacheDB', e)
    }
  }
  for (const idbName of dbsToDelete) {
    try {
      await deleteDB(idbName)
    } catch (e) {
      console.warn(`Error deleting indexDB ${idbName}`, e)
    }
  }
  try {
    await Auth.signOut();
    Hub.dispatch('logout', { event: 'logout' });
  } catch (e) {
    console.warn(e)
  }
  // Use a refresh query to possibly avoid a cached app reload
  Linking.openURL(`/?refresh=${Date.now()}`, '_self')
}

const App: React.FC = () => {
  return (
    <UserInit>
      <RightSideBar>
        <DNAAppContainer>
          <Idle />
          <DNAConnectedModal />
          <ContentPreviewModal />
          <PresentationBuilder />
          <RoutesListener />
          <Routes />
        </DNAAppContainer>
      </RightSideBar>
      <SlideSelector />
    </UserInit>
  );
};

const withProviders = ChildComponent => () => {
  const notifyError = (error) => {
    console.warn('Error encountered:', error)

    const { NODE_ENV } = process.env

    if (NODE_ENV && NODE_ENV !== 'development') {
      analytics?.track('APP_ERROR', {
        action: 'ERROR',
        category: 'APP',
        name: error.name,
        message: error.message,
      });

      const attributes = {
        segment_anonymous_id: analytics?.user().anonymousId(),
        user_id: analytics?.user().id() ?? '',
      }

      newrelic?.noticeError(error, attributes)
    }
  }

  // We keep this at the very top so that we can always get to /logout to force reload
  // the session
  if (window.location.pathname.startsWith('/logout')) {
    logout()
    return null;
  }

  return (
    <ErrorBoundary
      onError={notifyError}
      FallbackComponent={() => (
        <Error
          message={'Something has gone wrong, please reload this page!'}
        />
      )}
    >
      <ReduxProvider store={store}>
        <AppSettings>
          <Router>
            <AppContainer>
              <LuxTheming config={AlucioTheme}>
                <>
                  <BrowserChecker
                    unsupportedBrowserImage={UnsupportedBrowserImage}
                  >
                    {/* [TODO-PWA] - ALWAYS DISABLE THIS IN PRODUCTION */}
                    <DevLogger disable={true} />
                    <Authenticator>
                      <ChildComponent />
                    </Authenticator>
                  </BrowserChecker>
                </>
              </LuxTheming>
            </AppContainer>
          </Router>
        </AppSettings>
      </ReduxProvider>
    </ErrorBoundary>
  );
};

export default withLDProvider({
  clientSideID: config.LaunchDarklyKey,
  user: { key: 'AnonymousUser' },
},
)(withToastProvider(withProviders(App)));
